import React, { useEffect, useMemo } from 'react';
import { translate } from 'components/Translation';
import idConfig from 'utils/ecommerce/services/id.config';
import AccordionContent from '../../common/AccordionContent';
import extendRoamingInternational from './extendRoamingInternational';
import { getLanguage } from 'utils/language/language';

interface IMobileServicesContentWrapperProps {
	internationalEnabled: boolean;
	roamingInternationalEnabled: boolean;
	roamingInternationalPending: boolean;
	internationalPending: boolean;
	showOnlyActive: boolean;
	pendingStatusDate: string;
	updateServices: types.services.updateServicesFunction;
	internationalPackage: types.services.IPackageDetails;
	roamingPackage: types.services.IPackageDetails;
	setIsMobileServicesActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const MobileServicesContentWrapper = ({
	updateServices,
	showOnlyActive,
	roamingInternationalEnabled,
	internationalEnabled,
	roamingInternationalPending,
	internationalPending,
	pendingStatusDate,
	roamingPackage,
	internationalPackage,
	setIsMobileServicesActive,
}: IMobileServicesContentWrapperProps) => {
	const language = getLanguage();

	const gsmPackages: types.services.IPackage[] = useMemo(() => {
		return [
			{
				...roamingPackage,
				name: translate('number.services.international.roaming'),
				id: 'ROAMING_INTERNATIONAL',
				status: {
					type: roamingInternationalPending ? 'PENDING' : roamingInternationalEnabled ? 'ACTIVE' : 'INACTIVE',
					date: roamingInternationalPending ? pendingStatusDate : undefined,
				},
				legacyProductId: idConfig.ROAMING.legacyProductId,
				buttons: [
					{
						text: roamingInternationalEnabled
							? translate('numbers.services.turn.off')
							: translate('numbers.services.turn.on'),
						color: roamingInternationalEnabled ? 'tertiary-light' : 'primary-light',
						type: roamingInternationalEnabled ? 'TURN_OFF' : 'TURN_ON',
						disabled: internationalPending || roamingInternationalPending, // button disabled when one of roaming / international is pending
					},
				],
			},
			{
				...internationalPackage,
				name: translate('number.services.international'),
				id: 'INTERNATIONAL',
				status: {
					type: internationalPending ? 'PENDING' : internationalEnabled ? 'ACTIVE' : 'INACTIVE',
					date: internationalPending ? pendingStatusDate : undefined,
				},
				legacyProductId: idConfig.INTERNATIONAL.legacyProductId,
				tooltip: {
					content: translate('number.services.international.tooltip'),
				},
				buttons: [
					{
						text: internationalEnabled
							? translate('numbers.services.turn.off')
							: translate('numbers.services.turn.on'),
						color: internationalEnabled ? 'tertiary-light' : 'primary-light',
						type: internationalEnabled ? 'TURN_OFF' : 'TURN_ON',
						disabled: internationalPending || roamingInternationalPending, // button disabled when one of roaming / international is pending
					},
				],
			},
		];
	}, [
		roamingInternationalPending,
		internationalEnabled,
		roamingPackage,
		internationalPackage,
		roamingInternationalEnabled,
		internationalPending,
		pendingStatusDate,
		language,
	]);

	const filteredGsmPackages: types.services.IPackage[] = useMemo(() => {
		return showOnlyActive
			? gsmPackages.filter((singlePackage) => singlePackage.status?.type === 'ACTIVE')
			: gsmPackages;
	}, [gsmPackages, showOnlyActive]);

	const updateGsmServices = (singlePackage: types.services.IPackage) => {
		const orderItems: types.services.IOrderItem[] = [];
		let action: types.services.actionType = singlePackage.status?.type === 'ACTIVE' ? 'TURN_OFF' : 'TURN_ON';
		if (action === 'TURN_OFF') {
			orderItems.push({
				action: 'REMOVE',
				id: singlePackage.legacyProductId,
				place: singlePackage.place,
				ebuConfigProduct: singlePackage.ebuConfigProduct,
			});
			// if selected package is ROAMING we have to remove INTERNATIONAL
			if (singlePackage.legacyProductId === idConfig.ROAMING.legacyProductId) {
				orderItems.push({
					...internationalPackage,
					action: 'REMOVE',
					id: idConfig.INTERNATIONAL.legacyProductId,
				});
			}
		}
		// TURN ON
		else {
			// INTERNATIONAL
			if (singlePackage.legacyProductId === idConfig.INTERNATIONAL.legacyProductId) {
				// if ROAMING is enabled we just have to remove ROAMING package
				if (roamingInternationalEnabled) {
					orderItems.push({
						...roamingPackage,
						action: 'REMOVE',
						id: idConfig.ROAMING.legacyProductId,
					});
				} else {
					orderItems.push({
						action: 'ADD',
						id: singlePackage.legacyProductId,
						place: singlePackage.place,
						ebuConfigProduct: singlePackage.ebuConfigProduct,
					});
				}
			}
			// ROAMING
			else {
				orderItems.push({
					action: 'ADD',
					id: singlePackage.legacyProductId,
					place: singlePackage.place,
					ebuConfigProduct: singlePackage.ebuConfigProduct,
				});
				if (!internationalEnabled) {
					orderItems.push({
						...internationalPackage,
						action: 'ADD',
						id: idConfig.INTERNATIONAL.legacyProductId,
					});
				}
			}
		}
		updateServices(orderItems, singlePackage.name, action);
	};

	useEffect(() => {
		setIsMobileServicesActive(filteredGsmPackages.some((pckg) => pckg.status?.type === 'ACTIVE'));
	}, [filteredGsmPackages]);

	return (
		<AccordionContent
			elements={filteredGsmPackages}
			updateService={updateGsmServices}
			confirmations={[{ type: 'TURN_OFF' }, { type: 'TURN_ON' }]}
		/>
	);
};

export default extendRoamingInternational(MobileServicesContentWrapper);
